<template>
    <VVVLayoutNew>
        <template #page-title>
            {{ isAdminOrSuperAdmin ? 'Claims List' : 'My Claimed Videos' }}
        </template>
        
        <!-- IMPERSONATION BANNER -->
        <MDBAlert
            v-if="isImpersonating"
            color="info"
            static
            class="mb-4 d-flex align-items-center justify-content-between"
            style="background-color: #D7DEFF; color: var(--primary); font-weight: 600;"
        >
            <div class="d-flex align-items-center">
                <vue-feather class="mx-2" type="user" :stroke-width="2.5" />
                <span>
                    Viewing as
                    <b>{{ userImpersonateDetail.firstName }} {{ userImpersonateDetail.lastName }}</b>
                    ({{ userImpersonateDetail.email }})
                    [IMPERSONATING]
                </span>
            </div>

            <MDBBtn
                color="danger"
                size="sm"
                class="fw-bold"
                @click="stopImpersonation"
                style="cursor: pointer;"
            >
                Stop Impersonation
            </MDBBtn>
        </MDBAlert>
        <MDBCard id="ContentUploads" class="shadow-0">
            <MDBCardHeader class="py-2 px-0">
                <div class="container-fluid">
                    <div class="align-items-start">
                        <!-- First Row -->
                        <div v-if="isAdmin || isSuperAdmin"
                            class="d-flex flex-column flex-md-row align-items-center justify-content-between gap-2">
                            <div class="d-flex align-items-center gap-2 justify-content-start">

                                <!-- Sync Google Sheet for Admins -->
                                <template v-if="isAdminOrSuperAdmin">
                                    <div class="position-relative">
                                        <MDBInput v-model="search" label="Select Distributor" type="text"
                                            @keydown="handleBackspaceDistributor" size="sm" class="flex-grow-1" />

                                        <FilterSuggestDropdown
                                            :visible="isDistributorDropdownVisible && search.trim() && !selectedUserId"
                                            :loading="userListLoading"
                                            :items="userList"
                                            loading-message="Searching distributors…"
                                            empty-message="No distributors match your search."
                                            list-aria-label="Distributors"
                                            close-aria-label="Close distributor list"
                                            modifier-class="filter-suggest-dropdown--distributor"
                                            @close="isDistributorDropdownVisible = false"
                                            @select="selectUser" />
                                    </div>

                                    <MDBBtn color="primary" size="sm" @click="syncGoogleSheet(selectedUserId)"
                                        :disabled="!selectedUserId || isLoading">
                                        <MDBIcon iconStyle="fas" icon="rotate" />
                                        {{ isMobile ? 'Sync ' : 'Sync Google Sheet' }}
                                    </MDBBtn>
                                </template>

                                <!-- New Claim for Admin View -->
                                <MDBBtn color="primary" size="sm" @click="toggleAdd"
                                    :disabled="isLoading || (isAdminOrSuperAdmin && !selectedUserId)">
                                    <MDBIcon iconStyle="fas" icon="plus" /> {{ isMobile ? 'New ' : 'New Claim' }}
                                </MDBBtn>
                            </div>

                            <div class="d-flex justify-content-end align-items-center gap-2">

                                <!-- Claimant Search -->
                                <div v-if="isAdminOrSuperAdmin" class="position-relative flex-grow-1 flex-md-grow-0">
                                    <MDBInput v-model="searchClaimaintMain" label="Search by Claimant" type="text"
                                        size="sm" @keydown="handleClaimantMainBackspace" />
                                    <FilterSuggestDropdown
                                        :visible="isClaimantDropdownVisible && searchClaimaintMain.trim() && !searchClaimsWithClaimant"
                                        :loading="claimantListLoading"
                                        :items="claimantListMain"
                                        loading-message="Searching claimants…"
                                        empty-message="No claimants match your search."
                                        list-aria-label="Claimants"
                                        close-aria-label="Close claimant list"
                                        modifier-class="filter-suggest-dropdown--claimant"
                                        @close="isClaimantDropdownVisible = false"
                                        @select="selectClaimantMain" />
                                </div>

                                <!-- Content Search for Admin View -->
                                <div v-if="isAdmin || isSuperAdmin" class="flex-grow-1 flex-md-grow-0">
                                    <MDBInput v-model="searchFilter" label="Search Content" type="text" size="sm"
                                        @keydown="handleBackspaceContent" />
                                </div>
                            </div>
                        </div>

                        <!-- Second Row -->
                        <div class="gap-2 align-items-center "
                            :class="isMobile ? 'd-flex flex-column justify-content-start' : 'd-flex justify-content-between'">

                            <!-- Legends -->
                            <ClaimsListLegendNew :isAdmin="isAdminOrSuperAdmin" selected-user-id="selectedUserId"
                                @filterChanged="handleFilterChanged" @favoritesToggled="handleFavoriteClick" />

                            <div class="d-flex flex-column flex-md-row gap-2 align-items-center justify-content-end">
                                <div class="d-flex gap-2 align-items-center">
                                    <!-- Content Search for User View -->
                                    <div v-if="isUser" class="flex-grow-1 flex-md-grow-0 w-100">
                                        <MDBInput v-model="searchFilter" label="Search Content" type="text" size="sm"
                                            @keydown="handleBackspaceContent" />
                                    </div>

                                    <!-- Asset Label Filter -->
                                    <div v-if="isAdminOrSuperAdmin" class="position-relative w-100">
                                        <MDBInput v-model="searchAssetLabel" label="Select Asset Label" type="text"
                                            size="sm" @keydown="handleBackspaceAssetlabel" />
                                        <FilterSuggestDropdown
                                            :visible="isDropdownVisible && searchAssetLabel.trim()"
                                            :loading="assetLabelListLoading"
                                            :items="assetLabelList"
                                            :selected-count="selectedAssetLabelIds.length"
                                            loading-message="Searching labels…"
                                            empty-message="No asset labels match your search."
                                            list-aria-label="Asset labels"
                                            close-aria-label="Close asset label list"
                                            @close="isDropdownVisible = false">
                                            <template #header-actions>
                                                <button
                                                    v-if="selectedAssetLabelIds.length !== assetLabelList.length"
                                                    type="button" class="filter-suggest-dropdown-link"
                                                    @click="selectAllLabels">
                                                    Select all
                                                </button>
                                                <button v-else type="button"
                                                    class="filter-suggest-dropdown-link filter-suggest-dropdown-link-muted"
                                                    @click="clearAllLabels">
                                                    Clear all
                                                </button>
                                            </template>
                                            <template #option="{ item: assetLabel }">
                                                <label class="filter-suggest-dropdown-item"
                                                    :class="{ 'is-selected': selectedAssetLabelIds.includes(assetLabel.name) }">
                                                    <input type="checkbox" class="claims-checkbox"
                                                        :value="assetLabel.id"
                                                        :checked="selectedAssetLabelIds.includes(assetLabel.name)"
                                                        @change="onAssetLabelCheckboxChange($event, assetLabel)" />
                                                    <span class="filter-suggest-dropdown-name"
                                                        :title="assetLabel.name">
                                                        {{ assetLabel.name }}
                                                    </span>
                                                    <button v-if="isSuperAdmin" type="button"
                                                        class="filter-suggest-dropdown-favorite"
                                                        :class="{ 'is-favorite': assetLabel.isFavorite }"
                                                        :aria-label="assetLabel.isFavorite ? 'Remove from favorites' : 'Add to favorites'"
                                                        @click.prevent.stop="toggleFavorite(assetLabel)">
                                                        <MDBIcon iconStyle="fas" icon="star"
                                                            :class="assetLabel.isFavorite ? 'text-warning' : 'text-muted'" />
                                                    </button>
                                                </label>
                                            </template>
                                        </FilterSuggestDropdown>
                                    </div>

                                    <!-- Status Filter -->
                                    <div class="did-floating-label-content flex-grow-1 flex-md-grow-0 w-100">
                                        <select v-model="selectedFilter" class="did-floating-select">
                                            <option v-for="option in claimStatusFilter" :key="option.value"
                                                :value="option.value">
                                                {{ option.text }}
                                            </option>
                                        </select>
                                        <label class="did-floating-label">Filter by Claim Status</label>
                                    </div>
                                </div>

                                <!-- Report Button -->
                                <MDBBtn v-if="isAdminOrSuperAdmin" class="" color="primary" size="sm"
                                    :disabled="isLoading" @click="generateReportModal = true">
                                    <MDBIcon iconStyle="fas" icon="table" /> Generate Report
                                </MDBBtn>

                                <div class="d-flex gap-2 align-items-center">
                                    <!-- Sync for Normal Users -->
                                    <template v-if="isUser">
                                        <MDBBtn color="primary" size="sm" @click="syncGoogleSheet(userDetail.id)"
                                            :disabled="isLoading">
                                            <MDBIcon iconStyle="fas" icon="rotate" /> Sync Google Sheet
                                        </MDBBtn>
                                    </template>

                                    <!-- New Claim for User View -->
                                    <MDBBtn v-if="isUser" color="primary" size="sm" @click="toggleAdd"
                                        :disabled="isLoading || (isAdminOrSuperAdmin && !selectedUserId)">
                                        <MDBIcon iconStyle="fas" icon="plus" /> New Claim
                                    </MDBBtn>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </MDBCardHeader>

            <MDBCardBody class="p-0 overflow-hidden" style="display: grid; grid-template-rows: auto auto auto 1fr">
                <div class="position-relative d-inline-block" style="z-index: 10;" v-if="isAdminOrSuperAdmin">
                    <button class="btn btn-outline btn-sm text-uppercase text-white"
                        style="font-size: 13px; min-width: 90px; margin-top: 10px; margin-bottom: 10px; background-color: var(--primary);"
                        :disabled="!selectedIds || selectedIds.length === 0" @click="viewAssignmentModal()">
                        Assign Claimant
                    </button>
                </div>
                <div class="overflow-auto" ref="tableRef"
                    :id="isMobile ? isUser ? 'table-container' : 'table-container-admin' : 'table-container'">
                    <MDBTable hover class="align-middle position-relative" style="z-index: 1;">
                        <thead class="table-light">
                            <tr style="position:sticky; top:0; background:white; z-index:1040;">
                                <th v-if="isAdminOrSuperAdmin" scope="col" class="py-3 align-middle text-left"
                                    style="position: sticky; top: 0; padding-left: 30px;">
                                    <input type="checkbox" class="claims-checkbox" :checked="isAllSelected"
                                        @change="toggleSelectAll" />
                                </th>

                                <th scope="col" class="py-3 align-middle text-left">
                                    MANAGE CLAIM
                                </th>
                                <th scope="col" class="py-3 align-middle text-left" v-if="isAdminOrSuperAdmin"
                                    style="position: sticky; top: 0; padding-left: 23px;">
                                    DISTRIBUTOR
                                </th>
                                <th v-for="(header, i) in tableHeaders" :key="i" scope="col"
                                    class="py-3 align-middle text-left">
                                    <template v-if="header.includes('Date')">
                                        <span @click="toggleSort(header)" style="cursor: pointer;"
                                            class="fw-bold d-flex align-items-center gap-1">
                                            {{ header }}
                                            <span v-if="sortOrders[header.replace(/\s+/g, '')] === 'asc'">
                                                <vue-feather type="arrow-up" :stroke-width="3" size="15" class="pt-1" />
                                            </span>
                                            <span v-else>
                                                <vue-feather type="arrow-down" :stroke-width="3" size="15"
                                                    class="pt-1" />
                                            </span>
                                        </span>
                                    </template>
                                    <template v-else>
                                        {{ header }}
                                    </template>
                                </th>
                            </tr>
                        </thead>
                        <tbody class="overflow-auto">
                            <template v-if="isLoading">
                                <tr>
                                    <td :colspan="isAdminOrSuperAdmin ? '17' : '15'" class="text-center">
                                        <div class="image-container d-flex justify-content-center align-items-center">
                                            <div class="loader"></div>
                                            <img src="@/assets/VVV/vvv-loader.png" alt="Loading image">
                                        </div>
                                    </td>
                                </tr>
                            </template>
                            <template v-else>
                                <template v-if="contentsList.length === 0">
                                    <tr>
                                        <td :colspan="isAdminOrSuperAdmin ? '17' : '15'" class="text-left">
                                            No claims found.
                                        </td>
                                    </tr>
                                </template>
                                <template v-else>
                                    <ClaimsListTableRowNew v-for="(contents, k) in contentsList" :key="k"
                                        :contents="contents" :isSelected="(selectedIds || []).includes(contents.id)"
                                        @toggleSelection="onRowToggleSelection" :isAdmin="isAdminOrSuperAdmin"
                                        @viewContent="viewContent" />
                                </template>
                            </template>
                        </tbody>
                    </MDBTable>
                </div>

                <div class="d-md-flex d-block justify-content-between py-3 overflow-auto">
                    <Paginate v-if="contentsList.length != 0" :page-count="contentsListPagination.totalPages"
                        :click-handler="handlePage" :prev-text="'Previous'" :next-text="'Next'"
                        :container-class="'pagination'" first-last-button
                        style="margin-top: 0px; margin-left: unset; overflow: auto;cursor: pointer;">
                    </Paginate>
                    <MDBSelect v-if="contentsList.length != 0" v-model:options="pageSizeFilter"
                        v-model:selected="selectedPageSize" label="Page Size" size="sm" class="mt-lg-0 mt-4 py-lg-1" />
                </div>
            </MDBCardBody>
        </MDBCard>
        <MDBModal id="ViewContentModal" tabindex="-1" labelledby="ViewContentModal" v-model="showViewContentModal"
            size="lg">
            <MDBModalHeader class="px-4 py-3">
                <MDBModalTitle class="fw-bold">View Content</MDBModalTitle>
            </MDBModalHeader>
            <MDBModalBody class="px-4">
                <div class="d-lg-flex d-block gap-4">
                    <div v-if="!isUser" class="mb-3 w-100">
                        <label for="assetLabel" class="form-label">Asset Label</label>
                        <input type="text" class="form-control" id="assetLabel" placeholder="Enter Asset Label"
                            :value="selectedContent.assetLabel" disabled />
                    </div>
                    <div class="mb-3 w-100">
                        <label for="assetId" class="form-label">Asset ID</label>
                        <input type="text" class="form-control" id="assetId" placeholder="Enter Asset ID"
                            :value="selectedContent.assetId" disabled />
                    </div>
                </div>
                <div class="d-lg-flex d-block gap-4">
                    <div class="mb-3 title w-100">
                        <label for="youtubeChannel" class="form-label">Youtube Channel Name</label>
                        <input type="text" class="form-control" id="youtubeChannel"
                            :value="selectedContent.youtubeChannel" disabled />
                    </div>
                    <div class="mb-3 w-100">
                        <label for="youtubeUrl" class="form-label">Youtube Channel URL</label>
                        <input type="text" class="form-control" id="youtubeUrl" :value="selectedContent.youtubeUrl"
                            disabled />
                    </div>
                </div>
                <div class="d-lg-flex d-block gap-4">
                    <div class="mb-3 w-100">
                        <label for="claimStatus" class="form-label">Claim Status</label>
                        <input type="text" class="form-control" id="claimStatus"
                            :value="selectedContent.claimStatusName ? selectedContent.claimStatusName : '-'" disabled />
                    </div>
                    <div v-if="!isUser" class="mb-3 w-100">
                        <label for="claimant" class="form-label">Claimant</label>
                        <input type="text" class="form-control" id="claimant"
                            :value="selectedContent.claimant ? selectedContent.claimantFirstName + ' ' + selectedContent.claimantLastName : '-'"
                            disabled>
                    </div>
                    <div class="mb-3 w-100">
                        <label for="timeStamp" class="form-label">Time Stamp</label>
                        <input type="text" class="form-control" id="timeStamp" :value="selectedContent.timeStamp"
                            disabled>
                    </div>
                </div>
                <div class="d-lg-flex d-block gap-4">
                    <div class="mb-3 title w-100">
                        <label for="clipTitle" class="form-label">Clip Title</label>
                        <input type="text" class="form-control" id="clipTitle" :value="selectedContent.clipTitle"
                            disabled>
                    </div>
                    <div class="mb-3 w-100">
                        <label for="clipUsed" class="form-label">Clip Used</label>
                        <input type="text" class="form-control" id="clipUsed" :value="selectedContent.dropboxLink"
                            disabled>
                    </div>
                </div>
                <div v-if="!isUser" class="mb-3">
                    <label for="clipDescription" class="form-label">Clip Description</label>
                    <input type="text" class="form-control" id="clipDescription"
                        :value="selectedContent.internalAssetDescription" disabled />
                </div>
                <div class="mb-3">
                    <label for="reason-for-invalid" class="form-label">Reason for Invalid</label>
                    <textarea class="form-control" id="reason-for-invalid" :value="selectedContent.remarks" disabled
                        rows="3" />
                </div>
                <div class="mb-3">
                    <label for="staff-comments" class="form-label">Staff Comments</label>
                    <textarea class="form-control" id="staff-comments" :value="selectedContent.notes" disabled
                        rows="3" />
                </div>
                <div class="d-lg-flex d-block gap-4">
                    <div class="mb-3 w-100">
                        <label for="dateAdded" class="form-label">Date Requested</label>
                        <input type="text" class="form-control" id="dateAdded"
                            :value="parseDateLong(selectedContent.dateAdded)" disabled />
                    </div>
                    <div class="mb-3 w-100">
                        <label for="dateUpdated" class="form-label">Date Updated</label>
                        <input type="text" class="form-control" id="dateUpdated"
                            :value="parseDateLong(selectedContent.dateUpdated)" disabled />
                    </div>
                </div>
            </MDBModalBody>
            <MDBModalFooter class="px-4 py-3 d-flex justify-content-between align-items-center">
                <div style="min-width: 80px;">
                    <MDBBtn v-if="isAdminOrSuperAdmin" class="fw-bold" color="primary" size="sm"
                        @click="viewReclaimAssignmentModal(selectedContent)">
                        Reclaim
                    </MDBBtn>
                </div>

                <div class="d-flex gap-2">
                    <MDBBtn class="fw-bold" @click="showViewContentModal = false" size="sm">
                        Close
                    </MDBBtn>
                    <MDBBtn class="fw-bold" color="primary" size="sm" @click="toggleUpdate(selectedContent)">
                        Update Content
                    </MDBBtn>
                </div>
            </MDBModalFooter>

        </MDBModal>
        <MDBModal id="ViewContentModal" tabindex="-1" labelledby="ViewContentModal" v-model="showReclaimAssignmentModal"
            size="lg" style="z-index: 99999;">
            <MDBModalHeader class="px-4 py-3">
                <MDBModalTitle class="fw-bold">Reclaim this Content / Claim</MDBModalTitle>
            </MDBModalHeader>

            <MDBModalBody class="px-4">

                <div class="alert alert-warning d-flex align-items-start gap-2" role="alert">
                    <i class="fas fa-exclamation-triangle mt-1"></i>
                    <div>
                        <strong>Important:</strong> Reclaiming this content will create a <b>new line item</b>
                        with all data copied from the parent claim. You must assign a
                        <b>different Asset Label and Description</b> than the one used in the original claim.
                    </div>
                </div>

                <div class="mt-3">
                    <table>
                        <tr>
                            <td>{{ form.clipTitle }}</td>
                        </tr>
                    </table>
                    <label for="assetLabel" class="form-label fw-semibold">New Asset Label</label>
                    <select class="form-control" v-model="form.assetLabel">
                        <option v-for="label in userAssetLabels" :key="label.name" :value="label.name">
                            {{ label.name }}
                        </option>
                    </select>
                    <label for="assetLabel" class="form-label fw-semibold">New Clip Description</label>
                    <textarea v-model="form.internalAssetDescription" type="text" class="form-control" />

                </div>

            </MDBModalBody>

            <MDBModalFooter class="px-4 py-3 gap-2">
                <MDBBtn class="fw-bold" @click="showReclaimAssignmentModal = false" size="sm">Close</MDBBtn>
                <MDBBtn class="fw-bold" color="primary" size="sm" @click="handleReclaim">Save</MDBBtn>
            </MDBModalFooter>
        </MDBModal>


        <MDBModal id="ViewContentModal" tabindex="-1" labelledby="ViewContentModal" v-model="showViewAssignmentModal"
            size="lg" style="z-index: 99999;">
            <MDBModalHeader class="px-4 py-3">
                <MDBModalTitle class="fw-bold">Selected Claims</MDBModalTitle>
            </MDBModalHeader>
            <MDBModalBody class="px-4">
                <div style="position:relative;z-index: 9999;" v-if="isAdminOrSuperAdmin">
                    <div class="overflow-x-auto rounded-lg border border-gray-200">
                        <table class="min-w-full divide-y divide-gray-200 text-sm">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th class="px-4 py-2 text-left font-medium text-gray-600">Asset Label</th>
                                    <th class="px-4 py-2 text-left font-medium text-gray-600">User</th>
                                    <th class="px-4 py-2 text-left font-medium text-gray-600">YouTube Channel</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100">
                                <tr v-for="(asset, index) in selectedIds" :key="index"
                                    class="hover:bg-gray-50 transition">
                                    <td class="px-4 py-2 font-medium text-gray-800">
                                        {{ getAssetLabelsName(asset).assetLabel }}
                                    </td>
                                    <td class="px-4 py-2 text-gray-700">
                                        {{ getAssetLabelsName(asset).userName }}
                                    </td>
                                    <td class="px-4 py-2 text-gray-700 truncate max-w-xs">
                                        {{ getAssetLabelsName(asset).youtubeChannel }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <label for="assignbulk" class="mt-2">Update Claimant</label>
                    <input type="text" class="form-control" name="assignbulk" v-model="searchAssignee"
                        placeholder="Select Claimant" @keydown="handleClaimantBackspace" />
                    <MDBTable v-if="searchAssignee !== ''" responsive hover border="primary"
                        class="bg-white rounded-5 overflow-auto" style="position:absolute;left:0;">
                        <tbody>
                            <tr v-if="claimantListLoading">
                                <td class="text-center">Loading</td>
                            </tr>

                            <template v-else>
                                <tr v-for="(user, index) in claimantList" :key="index" @click="() => {
                                    assignSearchAssetClaimant = user.id;
                                    searchAssignee = user.firstName + ' ' + user.lastName;
                                }" style="cursor:pointer;">
                                    <td>{{ user.firstName + ' ' + user.lastName }}</td>
                                </tr>
                            </template>
                        </tbody>
                    </MDBTable>
                </div>
            </MDBModalBody>
            <MDBModalFooter class="px-4 py-3 gap-2">
                <MDBBtn class="fw-bold" @click="showViewContentModal = false" size="sm"> Close </MDBBtn>
                <MDBBtn class="fw-bold" color="primary" size="sm" @click="toggleUpdateClaimant()"> Update Claimant
                </MDBBtn>
            </MDBModalFooter>
        </MDBModal>
        <MDBModal id="FormContentModal" tabindex="-1" labelledby="FormContentModal" v-model="showFormContentModal"
            size="lg" :static-backdrop="true">
            <MDBModalHeader class="px-4 py-3">
                <MDBModalTitle class="fw-bold">
                    {{ isUpdateContent ? 'Update' : 'Add New' }} Content
                </MDBModalTitle>
            </MDBModalHeader>
            <MDBModalBody class="px-4">
                <div class="d-lg-flex d-block gap-4">
                    <div v-if="!isUser" class="mb-3 w-100">
                        <label for="assetLabel" class="form-label">Asset Label</label>
                        <template v-if="userAssetLabels.length > 1">
                            <select class="form-control" id="assetLabel" v-model="form.assetLabel">
                                <option disabled value="">Select Asset Label</option>
                                <option v-for="label in userAssetLabels" :key="label.name" :value="label.name">
                                    {{ label.name }}
                                </option>
                            </select>
                        </template>
                        <template v-else>
                            <input type="text" class="form-control" id="assetLabel" placeholder="Enter Asset Label"
                                :value="form.assetLabel" disabled />
                        </template>
                    </div>
                    <div class="mb-3 w-100">
                        <label for="assetId" class="form-label">Asset ID</label>
                        <input type="text" class="form-control" id="assetId" placeholder="Enter Asset ID"
                            :value="form.assetId" disabled v-if="isUser" />
                        <input type="text" class="form-control" id="assetId" placeholder="Enter Asset ID"
                            v-model="form.assetId" v-else />
                    </div>
                </div>
                <div class="d-lg-flex d-block gap-4">
                    <div class="mb-3 title w-100">
                        <label for="youtubeChannel" class="form-label">Youtube Channel Name</label>
                        <template v-if="isUser && isEditDisabled(selectedContent.claimStatus)">
                            <input type="text" class="form-control" id="youtubeChannel"
                                :value="selectedContent.youtubeChannel" ref="youtubeChannelInput" disabled />
                        </template>
                        <input v-else type="text" class="form-control" id="youtubeChannel"
                            placeholder="Enter Youtube Channel Name" v-model="form.youtubeChannel"
                            ref="youtubeChannelInput" />
                    </div>
                    <div class="mb-3 w-100">
                        <label for="youtubeUrl" class="form-label">Youtube Channel URL</label>
                        <template v-if="isUser && isEditDisabled(selectedContent.claimStatus)">
                            <input type="text" class="form-control" id="youtubeUrl" :value="form.youtubeUrl" disabled />
                        </template>
                        <input v-else type="text" class="form-control" id="youtubeUrl"
                            placeholder="Enter Youtube Channel URL" v-model="form.youtubeUrl" />
                    </div>
                </div>
                <div class="d-lg-flex d-block gap-4">
                    <div class="mb-3 w-100">
                        <label for="claimStatus" class="form-label">Claim Status</label>
                        <input type="text" class="form-control" id="claimStatus"
                            :value="form.claimStatusName ? form.claimStatusName : '-'" disabled
                            v-if="isUser && !isStatusEditableForDistributor(selectedContent.claimStatus)" />
                        <template v-else>
                            <select v-model="form.claimStatus" class="form-control" style="appearance: auto">
                                <option v-for="(status, index) in claimStatuses(selectedContent.claimStatus)"
                                    :key="index" :value="status.value">
                                    {{ status.text }}
                                </option>
                            </select>
                        </template>
                    </div>
                    <div v-if="!isUser" class="mb-3 w-100">
                        <label for="claimant" class="form-label">Claimant</label>
                        <input type="text" class="form-control" id="claimant"
                            :value="form.claimant ? form.claimantFirstName + ' ' + form.claimantLastName : '-'" disabled
                            v-if="isUser" />
                        <div style="position:relative;z-index: 4;" v-if="isAdminOrSuperAdmin">
                            <input type="text" class="form-control" v-model="searchClaimant"
                                placeholder="Select Claimant" @keydown="handleClaimantBackspace" />
                            <MDBTable v-if="searchClaimant !== '' && !form.claimant" responsive hover border="primary"
                                class="bg-white rounded-5 overflow-auto" style="position:absolute;left:0;">
                                <tbody>
                                    <tr v-if="claimantListLoading">
                                        <td class="text-center">Loading</td>
                                    </tr>
                                    <template v-else>
                                        <tr v-if="claimantList.length === 0 && !form.claimant == ''">
                                            <td class="text-center">No claimant found.</td>
                                        </tr>
                                        <template v-else>
                                            <tr v-for="(user, index) in claimantList" :key="index" @click="() => {
                                                form.claimant = user.id;
                                                searchClaimant = user.firstName + ' ' + user.lastName;
                                            }" style="cursor:pointer;">
                                                <td>{{ user.firstName + ' ' + user.lastName }}</td>
                                            </tr>
                                        </template>
                                    </template>
                                </tbody>
                            </MDBTable>
                        </div>
                    </div>
                    <div class="mb-3 w-100">
                        <label for="timeStamp" class="form-label">Time Stamp</label>
                        <template v-if="isUser && isEditDisabled(selectedContent.claimStatus)">
                            <input type="text" class="form-control" id="timeStamp" :value="form.timeStamp"
                                ref="timeStampInput" disabled />
                        </template>
                        <input v-else type="text" class="form-control" id="timeStamp" placeholder="Enter Time Stamp"
                            v-model="form.timeStamp" ref="timeStampInput" />
                    </div>
                </div>
                <div class="d-lg-flex d-block gap-4">
                    <div class="mb-3 title w-100">
                        <label for="clipTitle" class="form-label">Clip Title</label>
                        <template v-if="isUser && isEditDisabled(selectedContent.claimStatus)">
                            <input type="text" class="form-control" id="clipTitle" :value="form.clipTitle" disabled />
                        </template>
                        <input v-else type="text" class="form-control" id="clipTitle" placeholder="Enter Clip Title"
                            v-model="form.clipTitle" />
                    </div>
                    <div class="mb-3 w-100">
                        <label for="clipUsed" class="form-label">Clip Used</label>
                        <template v-if="isUser && isEditDisabled(selectedContent.claimStatus)">
                            <input type="text" class="form-control" id="clipUsed" :value="form.dropboxLink" disabled />
                        </template>
                        <input v-else type="text" class="form-control" id="clipUsed" placeholder="Enter Clip URL"
                            v-model="form.dropboxLink" />
                    </div>
                </div>
                <div v-if="!isUser" class="mb-3">
                    <label for="clipDescription" class="form-label">Clip Description</label>
                    <input type="text" class="form-control" id="clipDescription" placeholder="Enter Clip Description"
                        :value="form.internalAssetDescription" disabled />
                </div>
                <div class="mb-3">
                    <label for="invalid-for-reason-form" class="form-label">Reason for Invalid</label>
                    <input type="text" class="form-control" id="invalid-for-reason-form" :value="form.remarks" disabled
                        v-if="isUser" />
                    <template v-else>
                        <select v-model="form.remarks" class="form-control" style="appearance: auto"
                            id="invalid-for-reason-form">
                            <option disabled value="">Please select one</option>
                            <option v-for="(option, index) in remarkOptions" :key="index" :value="option">
                                {{ option }}
                            </option>
                        </select>
                    </template>
                </div>
                <div class="mb-3">
                    <label for="staff-comment-form" class="form-label">Staff Comments</label>
                    <textarea class="form-control" id="staff-comment-form" placeholder="Enter Staff Comments"
                        v-model="form.notes" :disabled="isUser" />
                </div>
                <div class="d-lg-flex d-block gap-4">
                    <div class="mb-3 w-100">
                        <label for="dateAdded" class="form-label">Date Requested</label>
                        <input type="text" class="form-control" id="dateAdded" :value="parseDateLong(form.dateAdded)"
                            disabled />
                    </div>
                    <div class="mb-3 w-100">
                        <label for="dateUpdated" class="form-label">Date Updated</label>
                        <input type="text" class="form-control" id="dateUpdated"
                            :value="parseDateLong(form.dateUpdated)" disabled />
                    </div>
                </div>
            </MDBModalBody>
            <MDBModalFooter class="px-4 py-3 gap-2">
                <MDBBtn class="fw-bold" @click="showFormContentModal = false" size="sm"> Close </MDBBtn>
                <MDBBtn class="fw-bold" color="primary" @click="submit" size="sm"> {{ isUpdateContent ? 'Confirm Update'
                    :
                    'Confirm Add'
                }}
                </MDBBtn>
            </MDBModalFooter>
        </MDBModal>
        <MDBModal v-model="generateReportModal" id="GenerateReportFilterModal" tabindex="-1"
            labelledby="GenerateReportFilterModal" size="lg" :static-backdrop="true">
            <MDBModalHeader class="px-4 py-3">
                <MDBModalTitle class="fw-bold" id="GenerateReportFilterModal">
                    Generate Report
                </MDBModalTitle>
            </MDBModalHeader>
            <MDBModalBody id="GenerateReportFilterModal" class="px-4 py-0">
                <MDBContainer fluid class="pt-3 pb-4">
                    <MDBRow class="d-lg-flex d-block">
                        <MDBCol class="mb-3 mb-lg-0">
                            <label for="filterDateFrom" class="form-label">Date From:</label>
                            <input v-model="generateReport.dateFrom" type="date" name="filterDateFrom"
                                id="filterDateFrom" class="form-control" />
                        </MDBCol>
                        <MDBCol class="mb-3 mb-lg-0">
                            <label for="filterDateTo" class="form-label">Date To:</label>
                            <input v-model="generateReport.dateTo" type="date" name="filterDateTo" id="filterDateTo"
                                class="form-control" />
                        </MDBCol>
                    </MDBRow>
                    <MDBRow class="d-lg-flex d-block mt-3 mb-2">
                        <MDBCol class="mb-3 mb-lg-0">
                            <label for="assetLabel" class="form-label">Asset Label</label>
                            <input type="text" class="form-control" id="assetLabel" placeholder="Enter Asset Label"
                                v-model="generateReport.assetLabel" />
                        </MDBCol>
                        <MDBCol class="mb-3 mb-lg-0">
                            <label for="claimStatus" class="form-label">Claim Status</label>
                            <select v-model="generateReport.claimStatus" class="form-control"
                                style="appearance: auto; margin-top: 1px">
                                <option v-for="(status, index) in claimStatuses(generateReport.claimStatus)"
                                    :key="index" :value="status.value">
                                    {{ status.text }}
                                </option>
                            </select>
                        </MDBCol>
                        <MDBCol class="mb-2 mb-lg-0">
                            <label for="claimant" class="form-label">Claimant</label>
                            <div style="position:relative;z-index: 4;">
                                <input type="text" class="form-control" v-model="searchClaimant"
                                    placeholder="Select Claimant" @keydown="handleClaimantBackspace" />
                                <MDBTable v-if="searchClaimant !== '' && !generateReport.claimant" responsive hover
                                    border="primary" class="bg-white rounded-5 overflow-auto"
                                    style="position:absolute;left:0;">
                                    <tbody>
                                        <tr v-if="claimantListLoading">
                                            <td class="text-center">Loading</td>
                                        </tr>
                                        <template v-else>
                                            <tr v-if="claimantList.length === 0">
                                                <td class="text-center">No claimant found.</td>
                                            </tr>
                                            <template v-else>
                                                <tr v-for="(user, index) in claimantList" :key="index" @click="() => {
                                                    generateReport.claimant = user.id;
                                                    searchClaimant = user.firstName + ' ' + user.lastName;
                                                }" style="cursor:pointer;">
                                                    <td>{{ user.firstName + ' ' + user.lastName }}</td>
                                                </tr>
                                            </template>
                                        </template>
                                    </tbody>
                                </MDBTable>
                            </div>
                        </MDBCol>
                    </MDBRow>
                </MDBContainer>
            </MDBModalBody>
            <MDBModalFooter class="px-4 py-3 gap-2">
                <MDBBtn size="sm" @click="generateReportModal = false" class="fw-bold text-capitalize">Close</MDBBtn>
                <MDBBtn @click="extractReport" size="sm" color="primary" class="fw-bold text-capitalize">
                    Extract Report
                </MDBBtn>
            </MDBModalFooter>
        </MDBModal>
        <MDBToast v-model="toastContentSuccess" :delay="2000" autohide position="top-right" appendToBody stacking
            width="350px" color="success" text="white" icon="fas fa-check fa-lg me-2">
            <template #title> Success </template>
            Content {{ isUpdateContent ? 'Updated' : 'Added' }} Successfully!
        </MDBToast>
        <MDBToast v-model="toastSheetSuccess" :delay="2000" autohide position="top-right" appendToBody stacking
            width="350px" color="success" text="white" icon="fas fa-check fa-lg me-2">
            <template #title> Success </template>
            Syncing Successful.
        </MDBToast>
        <MDBToast v-model="toastError" :delay="2000" autohide position="top-right" appendToBody stacking width="350px"
            color="danger" text="white" icon="fas fa-check fa-lg me-2">
            <template #title> Error </template>
            Fetching Failed.
        </MDBToast>
        <MDBToast v-model="toastErrorUpdate" :delay="2000" autohide position="top-right" appendToBody stacking
            width="350px" color="danger" text="white" icon="fas fa-check fa-lg me-2">
            <template #title> Error </template>
            <ul v-if="toastErrorUpdateMessage">
                <li v-for="(message, index) in toastErrorUpdateMessage" :key="index">{{ message }}</li>
            </ul>
        </MDBToast>
        <MDBToast v-model="generateReportToast" :delay="2000" autohide position="top-right" appendToBody stacking
            width="350px" :color="generateReportToastType === 'Error' ? 'danger' : 'success'" text="white"
            icon="fas fa-check fa-lg me-2">
            <template #title> {{ generateReportToastType }} </template>
            {{ generateReportToastMessage }}
        </MDBToast>
    </VVVLayoutNew>
</template>

<script setup>
import { useImpersonateStore } from "@/store/impersonateStore";
import { storeToRefs } from "pinia";
import { isDateInputValid, parseDateLong } from "@/helpers/parseDate";
import { isVVV } from "@/helpers/siteIdentifier";
import { GetContentsList } from "@/services/Contents/GetContentsList";
import { GetExportExcel } from "@/services/Contents/GetExportExcel";
import { GetGoogleSheetData } from "@/services/Contents/GetGoogleSheetData";
import { PostContent } from "@/services/Contents/PostContent";
import { PutClaimant } from "@/services/Contents/PutClaimant";
import { PutContent } from "@/services/Contents/PutContent";
import { GetFavoriteAssetLabel } from "@/services/FavoriteAssetLabels/GetFavoriteAssetLabel";
import { PostFavoriteAssetLabel } from "@/services/FavoriteAssetLabels/PostFavoriteAssetLabel";
import { GetUsers } from '@/services/Users/GetUsers';
import { useToastStore } from "@/store/toastStore";
import { useUserDetailStore } from "@/store/userDetailStore";

const impersonateStore = useImpersonateStore();
const { isImpersonating, userImpersonateDetail } = storeToRefs(impersonateStore);
const stopImpersonation = () => {
    impersonateStore.isImpersonating = false;
    impersonateStore.userImpersonateDetail = {
        email: "",
        firstName: "",
        id: "",
        lastName: "",
        userTypeId: "",
        isDistributor: false,
        googleSheetLink: null,
    };
    router.push("/users");
};


import { useYoutubeStore } from "@/store/youtubeStore";
import VVVLayoutNew from "@/views/VVVNew/VVVLayoutNew.vue";
import { useDebounceFn, useTitle, watchDebounced } from "@vueuse/core";
import { GetAssetLabels } from "@/services/AssetLabels/GetAssetLabels";
import axios from "axios";
import {
    MDBAlert,
    MDBBtn,
    MDBCard, MDBCardBody,
    MDBCardHeader,
    MDBCol,
    MDBContainer,
    MDBIcon,
    MDBInput,
    MDBModal,
    MDBModalBody, MDBModalFooter,
    MDBModalHeader, MDBModalTitle,
    MDBRow,
    MDBSelect,
    MDBTable,
    MDBToast
} from "mdb-vue-ui-kit";

import { computed, nextTick, onMounted, reactive, ref, watch, watchEffect } from 'vue';
import VueFeather from 'vue-feather';
import { useRoute, useRouter } from "vue-router";
import Paginate from "vuejs-paginate-next";
import { GetContentListFromArray } from "../../../services/Contents/GetContentListFromArray";
import ClaimsListLegendNew from "./ClaimsListLegendNew.vue";
import FilterSuggestDropdown from "./FilterSuggestDropdown.vue";
import ClaimsListTableRowNew from "./ClaimsListTableRowNew.vue";
import "./claims-checkbox.css";

const userDetailStore = useUserDetailStore();
const { userDetail, isAdmin, isUser, isSuperAdmin } = storeToRefs(userDetailStore);
const youtubeStore = useYoutubeStore();
const { isAdded } = storeToRefs(youtubeStore);
const selectedClaimFilter = ref('');
const selectedAssetLabelIds = ref([]);
const isDropdownVisible = ref(false)
const isDistributorDropdownVisible = ref(false)
const isClaimantDropdownVisible = ref(false)
const FILTER_SEARCH_DEBOUNCE_MS = 500
const selectedIds = ref([]);
const searchAssetClaimant = ref('');
const searchClaimsWithClaimant = ref('');
const sortOrders = reactive({});
const selectedSortColumn = ref("DateAdded");
const selectedSortOrder = ref("asc");
const searchAssignee = ref('');
const searchClaimaintMain = ref('');

function handleFilterChanged(filter) {
    selectedSortColumn.value = 'DateAdded';
    if (isUser.value) {
        if (filter === 'claimedbyme') {
            filter = 'claimedbyothers'
        }
    }
    if (filter === 'unclaimed') {
        if (isUser.value) {
            selectedSortOrder.value = 'desc';
        } else {
            selectedSortOrder.value = 'asc';
        }
    }
    if (filter === 'claimedbyme') {
        selectedSortOrder.value = 'desc';
        selectedSortColumn.value = 'DateClaimed';
    }
    selectedClaimFilter.value = filter;
    fetchContentsListArray({ pageNumber: 1 })
}
const isAdminOrSuperAdmin = computed(() => isAdmin.value || isSuperAdmin.value);


const getAssetLabelsName = (id) => {
    const asset = contentsList.value.find(c => String(c.id) === String(id));

    if (!asset) {
        return {
            assetLabel: `Unknown (ID: ${id})`,
            userName: '—',
            youtubeChannel: '—',
            profilePicture: '/default-avatar.png'
        };
    }

    return {
        assetLabel: asset.assetLabel || 'Unknown',
        userName: asset.userId
            ? `${asset.userFirstName || ''} ${asset.userLastName || ''}`.trim()
            : '—',
        youtubeChannel: asset.youtubeChannel || '—',
        profilePicture: asset.profilePictureUrl || '/default-avatar.png'
    };
}

const user = computed(() => userDetail.value);
const isAllSelected = computed(() => {
    if (contentsList.value.length === 0) return false;
    const visibleIds = contentsList.value.map(c => c.id);
    return visibleIds.every(id => selectedIds.value.includes(id));
});
const tableHeaders = computed(() => {
    if (isAdminOrSuperAdmin.value) {
        return [
            "Claim Request Timeline",
            "Claim Status",
            "Channel",
            "Youtube URL",
            "Timestamp",
            "Clip Used",
            "Clip Description",
            "Clip Title",
            "Asset ID",
            "Date Claimed",
            "Claimant",
            "Reason for Invalid",
            "Staff Comments",
            "Date Requested",
            "Date Updated",
        ];
    }
    return [
        "Claim Request Timeline",
        "Claim Status",
        "Channel",
        "Youtube URL",
        "Timestamp",
        "Clip Used",
        "Date Updated",
    ];
});

function handleFavoriteClick(isActive) {
    if (isActive) {
        const assetLabelNames = favoriteAssets.value.map(asset => asset.assetLabelName);
        selectedAssetLabelIds.value = assetLabelNames;
    } else {
        selectedAssetLabelIds.value = [];
    }

    fetchContentsListArray({ pageNumber: 1 });
}


watch(
    () => userDetailStore.userDetail,
    (newVal) => {
        localStorage.setItem('userDetail', JSON.stringify(newVal));
    },
    { deep: true }
)

const saved = localStorage.getItem('userDetail');
if (saved) {
    userDetailStore.userDetail = JSON.parse(saved);
}

const onAssetLabelCheckboxChange = (event, assetLabel) => {
    const { checked } = event.target;
    const name = assetLabel.name;

    if (checked) {
        if (!selectedAssetLabelIds.value.includes(name)) {
            selectedAssetLabelIds.value.push(name);
        }
    } else {
        selectedAssetLabelIds.value = selectedAssetLabelIds.value.filter(
            selectedName => selectedName !== name
        );
    }

    fetchContentsListArray({ pageNumber: 1 });
};


const isEditDisabled = (claimStatus) => {
    const disabledValues = ["1", "3", "6", "7"];
    return disabledValues.includes(claimStatus);
}

const isStatusEditableForDistributor = (currentStatus) => {
    return currentStatus === '2' || currentStatus === '7'
}

const handleBackspaceDistributor = (event) => {
    if (event.key === 'Backspace' && selectedUserId.value) {
        search.value = '';
        selectedUserId.value = null;
        userList.value = [];
        isDistributorDropdownVisible.value = false;
        fetchContentsList({ pageNumber: 1 })
        updateDistributorRoute();
    }
}

const claimStatuses = (currentStatus) => {
    const options = [
        { text: "Yes", value: '1' },
        { text: "No", value: '2' },
        { text: "Previously Claimed", value: '3' },
        { text: "Fixed", value: '4' },
        { text: "Not Yet", value: '5' },
        { text: "For Verification", value: '6' },
        // { text: "Awaiting for 100 Views", value: '7' }, // Remove temporarily
        { text: "Awaiting for at least 1 view", value: '8' },
    ];

    if (isUser.value && (isStatusEditableForDistributor(currentStatus))) {
        const currentOption = options.find(option => option.value === currentStatus);
        return [
            currentOption,
            { text: "Fixed", value: '4' },
        ];
    }

    return options;
}

const remarkOptions = ref([
    "None",
    "Clip embedded is not related to the compilation",
    "Dropbox link/clip provided is not included in the compilation/wrong timestamp",
    "Dropbox link unavailable",
    "Clip contains YT policy restriction",
    "Compilation contains YT policy restriction",
    "Manually claiming duet/stitch/ compilation is prohibited",
    "Video Unavailable",
    "Previously Claimed",
    "Must use less than 25 secs clip",
    "Channel on allowlist/whitelisted",
    "Compilation of single unlicensed creator is prohibited",
    "Incomplete Details",
    "Channel is registered to one of our distribution partners",
    "Should have at least 1 view",
    "Improper clip placement",
    "Video should not be unlisted",
]);

const claimStatusFilter = ref([
    { text: "All", value: '' },
    { text: "Yes", value: '1' },
    { text: "No", value: '2' },
    { text: "Previously Claimed", value: '3' },
    { text: "Fixed", value: '4' },
    { text: "Not Yet", value: '5' },
    { text: "For Verification", value: '6' },
    // { text: "Awaiting for 100 Views", value: '7' }, // Remove temporarily
    // { text: "Awaiting for at least 1 view", value: '8' }, // Remove temporarily, no longer supported
]);

const selectedFilter = ref('');
const searchFilter = ref('');
const isLoading = ref(false);
const contentsList = ref([]);
const favoriteAssets = ref([]);
const contentsListPagination = ref({});

const assignSearchAssetClaimant = ref('');
const selectedPageSize = ref(10);
const pageSizeFilter = ref([
    { text: "10", value: 10 },
    { text: "50", value: 50 },
    { text: "100", value: 100 }
]);

async function toggleUpdateClaimant() {
    const requestBody = {
        ContentIds: selectedIds.value,
        Claimant: assignSearchAssetClaimant.value,
        UserId: userDetail.value.id
    };

    try {
        await PutClaimant(requestBody);
        toastContentSuccess.value = true;
        if (isUser.value) fetchContentsListArray({ pageNumber: contentsListPagination.value.currentPage, userId: userDetail.value.id });
        else fetchContentsListArray({ pageNumber: contentsListPagination.value.currentPage, userId: selectedUserId.value });
        claimStatusBreakdown.value.loadBreakdown();
    } catch (error) {
        console.error('Error assigning claimant:', error);
        // Optional: Show error toast or alert
    }
}

function onRowToggleSelection(id, isSelected) {
    if (isSelected) {
        if (!selectedIds.value.includes(id)) {
            selectedIds.value.push(id);
        }
    } else {
        selectedIds.value = selectedIds.value.filter(selectedId => selectedId !== id);
    }
    
}



watch(selectedPageSize, (pageSizeValue) => {
    if (pageSizeValue !== null) {
        fetchContentsListArray({ pageNumber: 1 });
    }
});

watch(selectedFilter, (filterValue) => {
    if (filterValue !== undefined) {
        fetchContentsListArray({ pageNumber: 1 });
        updateClaimStatusRoute();
    }
});


watch(searchClaimsWithClaimant, (searchClaimsWithClaimantValue) => {
    if (searchClaimsWithClaimantValue !== '') {
        selectedClaimFilter.value = 'claimedbyme';
        fetchContentsListArray({ pageNumber: 1 });
        updateSearchContentRoute();
    }
});

watch(searchAssetClaimant, (searchAssetClaimantValue) => {
    if (searchAssetClaimantValue !== '') {
        fetchContentsListArray({ pageNumber: 1 });
        updateSearchContentRoute();
    }
});

watch(searchFilter, (searchFilterValue) => {
    if (searchFilterValue === '') {
        fetchContentsListArray({ pageNumber: 1 });
        updateSearchContentRoute();
    }
});

watchDebounced(
    searchFilter,
    (searchFilterValue) => {
        if (!searchFilterValue.trim()) {
            return;
        }
        fetchContentsListArray({ pageNumber: 1 });
        updateSearchContentRoute();
    },
    { debounce: FILTER_SEARCH_DEBOUNCE_MS }
);

watch(selectedClaimFilter, (selectedClaimFilterValue) => {
    if (selectedClaimFilterValue != '') {
        fetchContentsListArray({ pageNumber: 1 });
        updateSearchContentRoute();
    }
})

const handleBackspaceContent = (event) => {
    if (event.key === 'Backspace' && searchFilter.value) {
        searchFilter.value = '';
    }
}

const getFavoriteAssetLabels = async () => {
    const response = await GetFavoriteAssetLabel(912);
    favoriteAssets.value = response;
}


onMounted(() => {
    getFavoriteAssetLabels();
});

onMounted(() => {
    // if (isUser.value) {
    //     selectedClaimFilter.value = ''
    // } else {
    //     selectedClaimFilter.value = 'unclaimed';
    // }
    fetchContentsListArray({ pageNumber: 1 });
    useTitle(`${isAdminOrSuperAdmin.value ? 'Claims List' : 'My Claimed Videos'} | Very Viral Videos`);
    if (isAdded.value) {
        toastContentSuccess.value = true;
    }
});

watch(isAdminOrSuperAdmin, () => {
    useTitle(`${isAdminOrSuperAdmin.value ? 'Claims List' : 'My Claimed Videos'} | Very Viral Videos`);
    if (isAdminOrSuperAdmin.value) {
        getUsers();
    }
});

const scrollToTable = () => {
    nextTick(() => {
        const element = document.getElementById('ContentUploads');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    });
};

const toggleSort = (column) => {
    const cleanedColumn = column.replace(/\s+/g, '');

    // Determine what should be shown as the selected column
    const displayColumn = cleanedColumn === 'DateRequested' ? 'DateAdded' : cleanedColumn;

    if (!sortOrders[cleanedColumn]) {
        sortOrders[cleanedColumn] = 'asc';
    }

    if (selectedSortColumn.value === displayColumn) {
        selectedSortOrder.value = selectedSortOrder.value === 'asc' ? 'desc' : 'asc';
        sortOrders[cleanedColumn] = selectedSortOrder.value;
    } else {
        selectedSortColumn.value = displayColumn;
        selectedSortOrder.value = 'asc';
        sortOrders[cleanedColumn] = 'asc';
    }

    fetchContentsListArray({ pageNumber: 1 });
};

const handleReclaim = async () => {
    let response
    response = await PostContent({ ...form.value });
    if (response.status === 200) {
        showReclaimAssignmentModal.value = false;
    }
    toastContentSuccess.value = true;
};



const fetchContentsListArray = async ({ pageNumber }) => {
    isLoading.value = true;
    const params = {
        pageNumber: pageNumber,
        rowsPerPage: selectedPageSize.value,
        claimStatus: selectedFilter.value,
        searchString: searchFilter.value,
        assetLabels: selectedAssetLabelIds.value,
        claimedBy: selectedClaimFilter.value,
        claimant: searchClaimsWithClaimant.value,
        sortColumn: selectedSortColumn.value,
        sortOrder: selectedSortOrder.value,
    };

    if (selectedClaimFilter.value) {
        params.triggeredBy = userDetail.value.id;
    }

    // if (isAdminOrSuperAdmin.value) {
    //     params.userId = selectedUserId.value;
    // }
    if (isAdminOrSuperAdmin.value) {
    params.userId = selectedUserId.value;
    
    // ✅ NEW: Add isImpersonation flag if coming from impersonation
        if (route.query.isImpersonation === 'true') {
            params.isImpersonation = true;
        }
    }

    try {
        const response = await GetContentListFromArray(params);
        contentsList.value = response.data;
        contentsListPagination.value = response.pagination;
        scrollToTable();
    } catch (error) {
        console.error(error);
    } finally {
        isLoading.value = false;
    }
};

const fetchContentsList = async ({ pageNumber }) => {
    isLoading.value = true;
    const params = { pageNumber: pageNumber, rowsPerPage: selectedPageSize.value, claimStatus: selectedFilter.value, searchString: searchFilter.value, assetLabel: searchAssetLabel.value, claimedBy: selectedClaimFilter.value, claimant: searchClaimsWithClaimant.value };
    if (selectedClaimFilter.value) {
        params.triggeredBy = userDetail.value.id;
    }
    if (isAdminOrSuperAdmin.value) {
        params.userId = selectedUserId.value;
    }
    try {
        const response = await GetContentsList(params);
        contentsList.value = response.data;
        contentsListPagination.value = response.pagination;
        scrollToTable();
    } catch (error) {
        console.error(error);
    } finally {
        isLoading.value = false;
    }
};

const showViewContentModal = ref(false);
const showViewAssignmentModal = ref(false);
const showReclaimAssignmentModal = ref(false);
const selectedContent = ref({});
const userAssetLabels = ref([]);
const viewContent = async (content) => {
    isLoading.value = true

    try {
        showViewContentModal.value = true;
        selectedContent.value = content;
        const result = await GetAssetLabels(content.userId)
        userAssetLabels.value = result.data
        scrollToTable();
    } catch (error) {
        console.error(error);
    } finally {
        isLoading.value = false;
    }

};

const viewAssignmentModal = () => {
    showViewAssignmentModal.value = true;

}
const viewReclaimAssignmentModal = (content) => {
    if (content.claimant) { searchClaimant.value = content.claimantFirstName + ' ' + content.claimantLastName }
    form.value = { ...content };
    selectedContent.value = { ...content };
    showReclaimAssignmentModal.value = true;
}

const handlePage = (currentPage) => {
    event.preventDefault();
    if (isUser.value) fetchContentsListArray({ pageNumber: currentPage, userId: userDetail.value.id });
    else fetchContentsListArray({ pageNumber: currentPage, userId: selectedUserId.value });
}

const form = ref({
    assetLabel: "",
    youtubeChannel: "",
    youtubeUrl: "",
    timeStamp: "",
    clipTitle: "",
    claimStatus: "",
    claimStatusName: "",
    claimant: "",
    dropboxLink: "",
    assetId: "",
    internalAssetDescription: "",
    remarks: "",
    notes: "",
    dateAdded: "",
    dateUpdated: "",
});

const showFormContentModal = ref(false);
const toggleAdd = () => {
    if (isUser.value) {
        router.push({
            path: "/new-claim",
        })
    } else {
        showFormContentModal.value = true;
        isUpdateContent.value = false;
    }
}

const selectAllLabels = () => {
    selectedAssetLabelIds.value = assetLabelList.value.map(label => label.name);
    selectedAssetlabel.value = true;
    fetchContentsListArray({ pageNumber: 1 });
    updateAssetLabelRoute();
};

const clearAllLabels = () => {
    selectedAssetLabelIds.value = [];
    fetchContentsListArray({ pageNumber: 1 });
    updateAssetLabelRoute();
}

watch(showFormContentModal, (newShowFormContentModal) => {
    if (!newShowFormContentModal) {
        resetForm();
        searchClaimant.value = '';
        claimantList.value = [];
    }
});

const claimStatusBreakdown = ref();
const toastErrorUpdate = ref(false);
const toastErrorUpdateMessage = ref([]);

const submit = async () => {
    try {
        let response;
        if (isUpdateContent.value) {
            response = await PutContent({ ...form.value, ...(isAdminOrSuperAdmin.value ? { userId: selectedUserId.value } : {}) });
        } else {
            response = await PostContent({ ...form.value, ...(isAdminOrSuperAdmin.value ? { userId: selectedUserId.value } : {}) });
        }
        if (response && response.message) {
            let errorMessages = [];
            for (let key in response.errors) {
                errorMessages = errorMessages.concat(response.errors[key]);
            }
            toastErrorUpdate.value = true;
            toastErrorUpdateMessage.value = errorMessages;
            setTimeout(() => {
                toastErrorUpdate.value = false;
            }, 3000);
            return;
        }
        showFormContentModal.value = false;
        toastContentSuccess.value = true;
        if (isUser.value) fetchContentsListArray({ pageNumber: contentsListPagination.value.currentPage, userId: userDetail.value.id });
        else fetchContentsListArray({ pageNumber: contentsListPagination.value.currentPage, userId: selectedUserId.value });
        claimStatusBreakdown.value.loadBreakdown();
    } catch (error) {
        const errorMessage = error.data.message;
        const capitalizedErrorMessage = errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);
        toastErrorUpdate.value = true;
        toastErrorUpdateMessage.value = [capitalizedErrorMessage];
        setTimeout(() => {
            toastErrorUpdate.value = false;
        }, 3000);
        return;
    }
}

const resetForm = () => {
    form.value = {
        youtubeChannel: "",
        youtubeUrl: "",
        timeStamp: "",
        clipTitle: "",
        claimStatus: "",
        claimant: "",
        dropboxLink: "",
        assetId: "",
        assetLabel: "",
        internalAssetDescription: "",
        remarks: "",
        notes: "",
    }
}

const isUpdateContent = ref(false);
const updateContent = (content) => {
    if (content.claimant) { searchClaimant.value = content.claimantFirstName + ' ' + content.claimantLastName }
    form.value = { ...content };
    selectedContent.value = { ...content };
    isUpdateContent.value = true;
    showFormContentModal.value = true;
}

const toastContentSuccess = ref(false)
watch(toastContentSuccess, (newToastSuccess) => {
    if (!newToastSuccess) {
        isUpdateContent.value = false;
        isAdded.value = false;
    }
});

const toastError = ref(false);
const toastSheetSuccess = ref(false);
const syncGoogleSheet = async (id) => {
    if (toastSheetSuccess.value) toastSheetSuccess.value = false;
    isLoading.value = true;
    try {
        await GetGoogleSheetData(id);
        isLoading.value = false;
        toastSheetSuccess.value = true;
    } catch (error) {
        isLoading.value = false;
        toastError.value = true;
    }
}

const userList = ref([]);
const userListLoading = ref(false);
const toastStore = useToastStore();
const tableRef = ref(null);
const getUsers = async (searchVal = '') => {
    const currentDashboard = isVVV() ? 3 : 2;
    userListLoading.value = true;
    try {
        const response = await axios.get("api/users", {
            params: { SearchString: searchVal, UserTypeId: 3, CurrentDashboard: currentDashboard },
        });
        userList.value = response.data.data;
        userListLoading.value = false;
    } catch (error) {
        userListLoading.value = false;
        toastStore.toastObject = {
            ...toastStore.toastObject,
            ...toastStore.toastStatus.fail,
        };
    }
};

const searchAssetLabel = ref('');

watch(searchAssetLabel, (newSearch) => {
    if (!newSearch.trim()) {
        assetLabelList.value = [];
        assetLabelListLoading.value = false;
        isDropdownVisible.value = false;
        return;
    }
    isDropdownVisible.value = true;
    assetLabelListLoading.value = true;
});

watchDebounced(
    searchAssetLabel,
    (newSearch) => {
        if (newSearch === '') {
            return;
        }
        getAssetLabel(newSearch.toLowerCase());
    },
    { debounce: FILTER_SEARCH_DEBOUNCE_MS }
);

const search = ref('');
watch(search, (newSearch) => {
    if (!newSearch.trim()) {
        userList.value = [];
        userListLoading.value = false;
        isDistributorDropdownVisible.value = false;
        return;
    }
    if (selectedUserId.value) {
        return;
    }
    isDistributorDropdownVisible.value = true;
    userListLoading.value = true;
});

watchDebounced(
    search,
    (newSearch) => {
        if (!newSearch.trim() || selectedUserId.value) {
            return;
        }
        getUsers(newSearch.toLowerCase());
    },
    { debounce: FILTER_SEARCH_DEBOUNCE_MS }
);

const selectedUserId = ref(false);
const selectUser = (user) => {
    search.value = user.firstName + ' ' + user.lastName;
    selectedUserId.value = user.id;
    isDistributorDropdownVisible.value = false;
    fetchContentsListArray({ pageNumber: 1 })
    updateDistributorRoute();
}

const toggleSelectAll = (event) => {
    const checked = event.target.checked;
    const visibleIds = contentsList.value.map(c => c.id);

    if (checked) {
        selectedIds.value = Array.from(new Set([...selectedIds.value, ...visibleIds]));
    } else {
        selectedIds.value = selectedIds.value.filter(id => !visibleIds.includes(id));
    }
};


const handleBackspaceAssetlabel = (event) => {
    if (event.key === 'Backspace' && selectedAssetlabel.value) {
        isDropdownVisible.value = false;
        searchAssetLabel.value = '';
        selectedAssetlabel.value = false;
        fetchContentsList({ pageNumber: 1 });
        updateAssetLabelRoute();
    }
}

const searchClaimant = ref('');
const claimantList = ref([]);
const claimantListMain = ref([]);
const claimantListLoading = ref(false);
const getClaimantList = useDebounceFn(async (searchVal = '') => {
    claimantListLoading.value = true;
    try {
        const response = await axios.get("api/users?userTypeId=1&userTypeId=4", {
            params: { SearchString: searchVal }
        });
        claimantList.value = response.data.data;
        claimantListLoading.value = false;
    } catch (error) {
        claimantListLoading.value = false;
        toastStore.toastObject = {
            ...toastStore.toastObject,
            ...toastStore.toastStatus.fail,
        };
    }
}, 200);

const getClaimantListMain = async (searchVal = '') => {
    claimantListLoading.value = true;
    try {
        const response = await axios.get("api/users?userTypeId=1&userTypeId=4", {
            params: { SearchString: searchVal }
        });
        claimantListMain.value = response.data.data;
        claimantListLoading.value = false;
    } catch (error) {
        claimantListLoading.value = false;
        toastStore.toastObject = {
            ...toastStore.toastObject,
            ...toastStore.toastStatus.fail,
        };
    }
};

watch(searchClaimant, (newSearch) => {
    if (newSearch === '') {
        claimantList.value = [];
    } else {
        getClaimantList(newSearch.toLowerCase());
    }
});

watch(searchAssignee, (newSearch) => {
    if (newSearch === '') {
        claimantList.value = [];
    } else {
        getClaimantList(newSearch.toLowerCase());
    }
});

watch(searchClaimaintMain, (newSearch) => {
    if (!newSearch.trim()) {
        claimantListMain.value = [];
        claimantListLoading.value = false;
        isClaimantDropdownVisible.value = false;
        return;
    }
    if (searchClaimsWithClaimant.value) {
        return;
    }
    isClaimantDropdownVisible.value = true;
    claimantListLoading.value = true;
});

watchDebounced(
    searchClaimaintMain,
    (newSearch) => {
        if (!newSearch.trim() || searchClaimsWithClaimant.value) {
            return;
        }
        getClaimantListMain(newSearch.toLowerCase());
    },
    { debounce: FILTER_SEARCH_DEBOUNCE_MS }
);

const selectClaimantMain = (user) => {
    searchClaimsWithClaimant.value = user.id;
    searchClaimaintMain.value = user.firstName + ' ' + user.lastName;
    isClaimantDropdownVisible.value = false;
};

const handleClaimantMainBackspace = (event) => {
    if (event.key === 'Backspace' && searchClaimsWithClaimant.value) {
        searchClaimaintMain.value = '';
        searchClaimsWithClaimant.value = '';
        claimantListMain.value = [];
        isClaimantDropdownVisible.value = false;
        fetchContentsListArray({ pageNumber: 1 });
    }
};

watch(searchAssetClaimant, (newSearch) => {
    if (newSearch === '') {
        searchClaimsWithClaimant.value = '';
    } else {
        searchClaimsWithClaimant.value = newSearch;
    }
});

const timeStampInput = ref();
const youtubeChannelInput = ref();
const toggleUpdate = (selectedContent) => {
    updateContent(selectedContent);
    showViewContentModal.value = false;
}

watch(showFormContentModal, (newShowFormContentModal) => {
    if (newShowFormContentModal) {
        if (isAdminOrSuperAdmin.value) {
            setTimeout(() => {
                timeStampInput.value.focus();
            }, 200);
        }
        if (isUser.value) {
            setTimeout(() => {
                youtubeChannelInput.value.focus();
            }, 200);
        }
    }
});

const handleClaimantBackspace = () => {
    if (event.key === 'Backspace' && form.value.claimant) {
        form.value = { ...form.value, claimant: '' };
        searchClaimant.value = '';
        claimantList.value = [];
    }
}


const toggleFavorite = async (assetLabel) => {
    const userId = user.value.id;

    const originalValue = assetLabel.isFavorite;
    assetLabel.isFavorite = !originalValue;
    assetLabel.wasToggled = true;

    const payload = {
        userId,
        assetLabelId: assetLabel.id,
        toggled: assetLabel.isFavorite,
    };

    try {
        await PostFavoriteAssetLabel(payload);

        const index = favoriteAssets.value.findIndex(fav => fav.assetLabelId === assetLabel.id);
        if (payload.toggled && index === -1) {
            favoriteAssets.value.push({
                assetLabelId: assetLabel.id,
                assetLabelName: assetLabel.name,
            });
        } else if (!payload.toggled && index !== -1) {
            favoriteAssets.value.splice(index, 1);
        }

    } catch (error) {
        assetLabel.isFavorite = originalValue;
    }
};



const generateReport = ref({
    dateFrom: "",
    dateTo: "",
    assetLabel: "",
    claimStatus: "",
    claimant: ""
});

const generateReportModal = ref(false);
watch(generateReportModal, (newGenerateReportModal) => {
    if (!newGenerateReportModal) {
        generateReport.value = {
            dateFrom: "",
            dateTo: "",
            assetLabel: "",
            claimStatus: "",
            claimant: ""
        }
        searchClaimant.value = '';
    }
});

const generateReportToast = ref(false);
const generateReportToastType = ref("Error");
const generateReportToastMessage = ref("");

const showExportToast = (type, message) => {
    generateReportToast.value = true;
    generateReportToastType.value = type;
    generateReportToastMessage.value = message;
}

const extractReport = async () => {
    const reportData = generateReport.value;
    const { dateFrom, dateTo, assetLabel, claimStatus, claimant } = reportData;

    if (!validateExportFields({ dateFrom, dateTo, assetLabel, claimStatus, claimant })) {
        return;
    }

    const { url } = await GetExportExcel(reportData);
    showExportToast('Success', 'Download Successfully!');
    generateReportModal.value = false;
    window.open(url, '_blank');
};

const validateExportFields = ({ dateFrom, dateTo, assetLabel, claimStatus, claimant }) => {
    const fields = { dateFrom, dateTo, assetLabel, claimStatus, claimant };
    const filledField = Object.values(fields).some(value => value !== "" && value !== null);

    if (!filledField) {
        showExportToast('Error', 'At least one filter must be applied.');
        return false;
    }

    if ((!dateFrom && dateTo) || (dateFrom && !dateTo)) {
        showExportToast('Error', 'Both "Date From" and "Date To" must be filled.');
        return false;
    }

    if (dateFrom && !isDateInputValid(dateFrom)) {
        showExportToast('Error', 'Invalid "Date From". Please use a valid format for MM/DD/YYYY.');
        return false;
    }

    if (dateTo && !isDateInputValid(dateTo)) {
        showExportToast('Error', 'Invalid "Date To". Please use a valid format for MM/DD/YYYY.');
        return false;
    }

    if (dateFrom && dateTo && new Date(dateTo) <= new Date(dateFrom)) {
        showExportToast('Error', 'Ensure "Date To" is after "Date From".');
        return false;
    }

    return true;
};

const router = useRouter();
const route = useRoute();

const updateClaimStatusRoute = () => {
    if (selectedFilter.value) {
        router.push({
            path: '/claims-list',
            query: { claimStatus: selectedFilter.value }
        });
    } else {
        router.push({
            path: "/claims-list",
            name: "ClaimsList",
        })
    }
};

if (route.query.claimStatus) {
    selectedFilter.value = route.query.claimStatus;
}

watch(() => route.query.claimStatus, (claimStatusValue) => {
    if (claimStatusValue !== undefined && claimStatusValue !== null && selectedFilter.value !== claimStatusValue) {
        selectedFilter.value = claimStatusValue;
        fetchContentsList({ pageNumber: 1 });
        // updateClaimStatusRoute();
    }
});
 //added
watch(() => route.query.userId, (userId) => {
    
    if (userId && route.query.isImpersonation === 'true') {
        selectedUserId.value = parseInt(userId);
        
        // Fetch user details to show in search field
        GetUsers(userId).then(user => {
            search.value = user.firstName + ' ' + user.lastName;
        }).catch(error => {
            console.error("Error fetching user:", error);
        });
        
        fetchContentsListArray({ pageNumber: 1 });
    }
}, { immediate: true });
//added
watch(() => route.query.isImpersonation, (isImpersonation) => {

    
    if (isImpersonation === 'true' && route.query.userId) {
        fetchContentsListArray({ pageNumber: 1 });
    }
}, { immediate: true });

const updateSearchContentRoute = () => {
    if (searchFilter.value) {
        router.push({
            path: '/claims-list',
            query: { searchContent: searchFilter.value }
        });
    } else {
        router.push({
            path: "/claims-list",
            name: "ClaimsList",
        })
    }
};

if (route.query.searchContent) {
    searchFilter.value = route.query.searchContent;
}

watch(() => route.query.searchContent, (searchValue) => {
    if (searchValue !== undefined && searchValue !== null && searchFilter.value !== '') {
        searchFilter.value = searchValue;
        fetchContentsList({ pageNumber: 1 });
        updateSearchContentRoute();
    }
});

const updateDistributorRoute = () => {
    if (selectedUserId.value) {
        router.push({
            path: '/claims-list',
            query: { distributor: selectedUserId.value }
        });
    } else {
        router.push({
            path: "/claims-list",
            name: "ClaimsList",
        })
    }
};

if (route.query.distributor) {
    selectedUserId.value = route.query.distributor;
}

watch(() => route.query.distributor, (userId) => {
    if (userId !== undefined && userId !== null && selectedUserId.value !== userId) {
        GetUsers(userId)
            .then(user => {
                search.value = user.firstName + ' ' + user.lastName;
                selectedUserId.value = user.id;
                fetchContentsList({ pageNumber: 1 });
                updateDistributorRoute();
            });
    } else {
        search.value = '';
        selectedUserId.value = null;
    }
});

const updateAssetLabelRoute = () => {
    if (searchAssetLabel.value) {
        router.push({
            path: '/claims-list',
            query: { assetLabel: searchAssetLabel.value }
        });
    } else {
        router.push({
            path: "/claims-list",
            name: "ClaimsList",
        })
    }
};

if (route.query.assetLabel) {
    searchAssetLabel.value = route.query.assetLabel;
}

watch(() => route.query.assetLabel, (assetLabelSearch) => {
    if (assetLabelSearch !== undefined && assetLabelSearch !== null && searchAssetLabel.value !== '') {
        searchAssetLabel.value = assetLabelSearch;
        fetchContentsList({ pageNumber: 1 });
        updateAssetLabelRoute();
    }
});

const isMobile = ref(window.innerWidth <= 767);
const contentInputWidth = ref('');
const distriInputStyle = ref('');
const assetLabelInputStyle = ref('');
const claimantInputStyle = ref('');

watchEffect(() => {
    contentInputWidth.value = isMobile.value ? { width: '100%' } : (isUser.value ? { width: '33%' } : { width: '19%' });
    assetLabelInputStyle.value = isMobile.value ? { position: 'relative', zIndex: 4 }
        : { position: 'relative', zIndex: 4, width: '21%' };
    distriInputStyle.value = isMobile.value ? { position: 'relative', zIndex: 4 }
        : { position: 'relative', zIndex: 4 };
    claimantInputStyle.value = isMobile.value ? { width: '20%' } : (isUser.value ? { width: '33%' } : { width: '19%' });
});

const assetLabelList = ref([]);
const assetLabelListLoading = ref(false);
const getAssetLabel = async (searchVal = '') => {
    assetLabelListLoading.value = true;
    try {
        const response = await axios.get("api/assetLabels/List", {
            params: { SearchString: searchVal, rowsPerPage: 100, pageNumber: 1 },
        });
        assetLabelList.value = response.data.data.map(label => {
            const isFavorite = favoriteAssets.value.some(fav => fav.assetLabelId === label.id);
            return {
                ...label,
                isFavorite: isFavorite,
                wasToggled: false,
                isDeleted: !isFavorite
            };
        });

        assetLabelListLoading.value = false;
    } catch (error) {
        assetLabelListLoading.value = false;
        toastStore.toastObject = {
            ...toastStore.toastObject,
            ...toastStore.toastStatus.fail,
        };
    }
};

const selectedAssetlabel = ref(false);
</script>

<style scoped>
/* Impersonation Alert Styles */
.alert-container {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
}
.title {
    width: 47.5%;
}

.btn-primary {
    background-color: var(--primary);
    display: flex;
    align-items: center;
    gap: 5px;
}

.content-title {
    font-weight: bold;
    width: 200px;
    text-align: right;
    padding-right: 10px;
}

.content-info {
    width: 200px;
    text-align: left;
    padding-left: 10px;
}

.pagination {
    margin-top: 2rem;
    margin-bottom: 0;
    margin-left: auto;
}

#actions {
    flex: 0 0 55px;
}

#ContentUploads {
    flex: 1 1 auto;
    overflow: hidden;
}

#table-container {
    flex: 1 1 auto;
    max-height: calc(100vh - 200px);
}

.claim-status-select {
    width: fit-content;
}

/* customized select with floating label */
.did-floating-label-content {
    position: relative;
}

.did-floating-label {
    color: #1e4c82;
    font-size: 13px;
    font-weight: normal;
    position: absolute;
    pointer-events: none;
    left: 8.2px;
    top: 11px;
    padding: 0 5px;
    background: #fff;
    transition: 0.2s ease all;
    -moz-transition: 0.2s ease all;
    -webkit-transition: 0.2s ease all;
}

.did-floating-input,
.did-floating-select {
    font-size: 12px;
    display: block;
    width: 100%;
    height: 28.5px;
    padding: 0 13px;
    padding-right: 10px;
    background: #fff;
    color: #6c757d;
    border: 1px solid #adb5bd;
    border-radius: 4px;
    box-sizing: border-box;

    &:focus {
        outline: 1.5px solid #0d6efd;

        ~.did-floating-label {
            color: #0d6efd;
            top: -8px;
            font-size: 10px;
        }
    }
}

select.did-floating-select {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
}

select.did-floating-select::-ms-expand {
    display: none;
}

.did-floating-input:not(:placeholder-shown)~.did-floating-label {
    top: -8px;
    font-size: 10px;
}

.did-floating-select:not([value=""]):valid~.did-floating-label {
    top: -8px;
    font-size: 10px;
}

.did-floating-select[value=""]:focus~.did-floating-label {
    top: 11px;
    font-size: 13px;
}

.did-floating-select:not([multiple]):not([size]) {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='6' viewBox='0 0 8 6'%3E%3Cpath id='Path_1' data-name='Path 1' d='M371,294l4,6,4-6Z' transform='translate(-371 -294)' fill='%23003d71'/%3E%3C/svg%3E%0A");
    background-position: right 15px top 50%;
    background-repeat: no-repeat;
}

td {
    text-wrap: nowrap;
    font-size: 13px;
}

th {
    text-wrap: nowrap;
    max-width: 30%;
    font-size: 13px;
    z-index: 0;
}

@media screen and (max-width: 767px) {

    #actions {
        flex: 0 0 auto;
    }

    #ContentUploads {
        flex: 0 0 auto;
        overflow: hidden;
    }

    #table-container {
        flex: 1 1 auto;
        max-height: calc(100vh - 360px);
    }

    #table-container-admin {
        flex: 1 1 auto;
        max-height: calc(100vh - 500px);
    }

    #table-container-header {
        z-index: 0;
    }

    .claim-status-select {
        width: 100%;
    }

    .did-floating-input,
    .did-floating-select {
        width: 100%;
    }
}

.image-container {
    position: relative;
    display: inline-block;
}

.image-container .loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    width: 45px;
    height: 45px;
    border: 3px solid rgba(0, 0, 0, 0.1);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: BorderSpin 1s linear infinite;
}

.image-container img {
    display: block;
    width: 45px;
    height: 45px;
    border-radius: 50%;
}

@keyframes BorderSpin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}


</style>
